/**
 * Reference Analysis API Client
 * Handles analysis of references in report statements
 */

import {API_BASE} from './reports';
import buildHeaders from './fetchHeaders';

// Timeout for reference analysis (5 minutes)
const ANALYSIS_TIMEOUT = 5 * 60 * 1000;

/**
 * Fetch with timeout support
 */
const fetchWithTimeout = async (url, options = {}, timeout = ANALYSIS_TIMEOUT) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            ...options,
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            throw new Error(`Request timeout after ${timeout / 1000} seconds`);
        }
        throw error;
    }
};

export const findStatements = async (doc) => {
    try {
        console.log('📤 Finding Statemens...');
        console.log(doc)

        const response = await fetchWithTimeout(`${API_BASE}/analysis/statements`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify(doc)
        });

        if (!response.ok) {
            throw new Error(`Reference analysis failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Reference analysis complete');
        return result;

    } catch (error) {
        console.error('❌ Error analyzing references:', error);
        throw error;
    }
};

/**
 * Analyze references in report statements
 * For statements with references: evaluate the quality of those references
 * For conclusions: find if proven by other statements in the report
 *
 * @param {string} reportId - Report ID
 * @param {Array} values - Array of blocks with content and references
 * @returns {Promise<Object>} Analysis response
 *
 * Request format:
 * {
 *   "reportId": "report-123",
 *   "values": [
 *     {
 *       "blockId": "block-uuid-1",
 *       "content": [
 *         {
 *           "type": "paragraph",
 *           "attrs": {
 *             "classificationLevel": "U",
 *             "compartment": [],
 *             "distribution": [],
 *             "blockId": "block-uuid-1"
 *           },
 *           "content": [
 *             {
 *               "type": "text",
 *               "text": "Russia produced 1500 tanks in 2025."
 *             }
 *           ]
 *         }
 *       ],
 *       "references": [
 *         {
 *           "referenceMarkId": "ref-mark-id-1",
 *           "range": [0, 40],
 *           "referencedTexts": [
 *             "Russian defense ministry reported tank production...",
 *             "Independent analysis confirms manufacturing data..."
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 */
export const analyzeReferences = async (reportId, values) => {
    try {
        console.log('📤 Analyzing references...');
        console.log({
            reportId,
            values
        })

        const response = await fetchWithTimeout(`${API_BASE}/analysis`, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                reportId,
                values
            })
        });

        if (!response.ok) {
            throw new Error(`Reference analysis failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Reference analysis complete');
        return result;

    } catch (error) {
        console.error('❌ Error analyzing references:', error);
        throw error;
    }
};
